<template>
  <div
    :class="[
      'card-content-wrapper',
      {
        'card-content-wrapper__legends':
          !(activeCard.components && activeCard.components.includes('toggles')),
      },
    ]"
  >
    <div class="card-content-container">
      <div
        v-for="(vis, index) of activeCard.content"
        :key="vis.id"
        :class="[
          'small-card__main',
          'small-card__content-' + activeCard.content.length,
          { 'small-card__content-tabs': activeIndex >= 0 },
          { 'small-card--expand': smallExpanded[vis.id] },
          tabVisible(index),
        ]"
      >
        <p
          v-if="isChartType(vis.type)"
          v-html="vis.config.title"
          :class="[
            'small-card__title',
            'small-card__title__chart',
            { 'small-card__title__tile': activeCard.content.length > 1 }
          ]"></p>
        <div class="small-card__controls">
          <div v-if="!smallExpanded[vis.id]">
            <SvgExpand
              v-if="enableExpansion && activeCard.content.length > 1 && activeIndex === -1"
              @click="toggleExpand(vis.id)"
              class="small-card__small-svg"
            />
          </div>
          <div v-else>
            <SvgClose
              v-if="activeCard.content.length > 1 && activeIndex === -1"
              @click="toggleExpand(vis.id)"
              class="small-card__expanded-svg"
            />
          </div>
        </div>

        <TableViewer
          v-if="vis.type === 'table'"
          :title="vis.config.title"
          :content-id="vis.id"
          :content-index="index"
        />

        <ImageViewer
          v-else-if="vis.type === 'image'"
          :title="vis.config.title"
          :content-id="vis.id"
          :resource="vis.resource"
          :footnotes="vis.config.footnotes"
          :content-index="index"
        />

        <div v-else class="outer-chart-container">
          <div class="log" v-if="vis.type == 'table'">
            <pre class="pre">{{ vis.config.footnotes }}</pre>
          </div>

          <!-- TODO: refactor this templating pattern.
        This templating and layout styles shouldn't be considered final,
        it's just a quick placeholder to get visible charts in the tool.
      -->

          <div
            class="chart-container bar"
            v-if="vis.type == 'bar' || vis.type == 'bar-stacked'"
          >
            <BarChart
              class="chart"
              :config="adjustBarChartConfigBasedOnExpansion(vis.config, smallExpanded[vis.id])"
              :data="vis.data"
              :outerWidth="$chartDataService.getOuterWidth(vis.config, activeCard, smallExpanded[vis.id])"
              :outerHeight="$chartDataService.getOuterHeight(vis.config, activeCard, smallExpanded[vis.id])"
              :margin="getChartMargin(vis, $mq)"
            ></BarChart>
          </div>

          <div
            class="chart-container line"
            v-if="vis.type == 'line'"
          >
            <LineChart
              class="chart"
              :config="vis.config"
              :data="vis.data"
              :margin="getChartMargin(vis, $mq)"
            ></LineChart>
          </div>

          <div
            class="chart-container dotplot"
            v-if="vis.type == 'dotplot'"
          >
            <DotplotChart
              class="chart"
              :config="vis.config"
              :data="vis.data"
              :margin="getChartMargin(vis, $mq)"
            ></DotplotChart>
          </div>

          <div
            class="chart-container boxplot"
            v-if="vis.type === 'boxplot'"
          >
            <BoxplotChart
              class="chart"
              :config="vis.config"
              :data="vis.data"
              :margin="getChartMargin(vis, $mq)"
            ></BoxplotChart>
          </div>

          <ChartLegend
            class="legend"
            :cardData="activeCard"
            :vis="vis"
            v-if="shouldDisplayLegend(activeCard, index)"
            :bottom-margin="computeBottomLegend(vis.config.footnotes)"
          />

          <ChartToggle
            v-if="activeCard && activeCard.components && activeCard.components.includes('toggles')"
          />

          <Footnotes
            v-if="vis.config.footnotes.length > 0"
            class="footer"
            :vis-id="vis.id"
            :vis-index="index"
            :force-inline="smallExpanded[vis.id]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ViewSelector',
  components: {
    SvgExpand: () => import('@/assets/svgs/expand.svg?inline'),
    SvgClose: () => import('@/assets/svgs/close.svg?inline'),
    TableViewer: () => import('./TableViewer.vue'),
    ImageViewer: () => import('@/components/viewers/ImageViewer.vue'),
    ChartToggle: () => import('./ChartToggle.vue'),
    ChartLegend: () => import('./ChartLegend.vue'),
    Footnotes: () => import('./Footnotes.vue'),
  },
  props: {
    activeIndex: {
      type: Number,
      default: () => -1,
    },
    enableExpansion: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      smallExpanded: {},
    };
  },
  computed: {
    ...mapGetters([
      'activeCard',
    ]),
  },
  watch: {
    activeCard(newActiveCard) {
      this.resetSmallExpanded(newActiveCard)
    }
  },
  mounted() {
    this.resetSmallExpanded(this.activeCard)
  },
  methods: {
    getChartMargin(vis, $mq) {
      return this.$chartDataService.getMargin(vis.config, $mq);
    },

    isChartType(type) {
      return ['bar', 'bar-stacked', 'line', 'dotplot', 'boxplot'].includes(type);
    },

    /* This is a workaround since the charts do not support responsiveness yet */
    adjustBarChartConfigBasedOnExpansion(config, expanded) {
      if (config?.yAxis?.arrow?.up) {
        config.yAxis.arrow.up = {
          ...config.yAxis.arrow.up,
          fontSize: expanded ? "12px" : "9px"
        };
      }

      return { ...config };
    },

    resetSmallExpanded(card) {
      card?.content?.forEach((data) => {
        this.$set(this.smallExpanded, data.id, false);
      });
    },

    toggleExpand(id) {
      this.smallExpanded[id] = !this.smallExpanded[id];
    },

    tabVisible(index) {
      if (this.activeIndex >= 0) {
        if (this.activeIndex === index) {
          return '';
        } else {
          return 'tab-hidden';
        }
      } else {
        return '';
      }
    },

    computeBottomLegend(footnotes) {
      return 25 + (footnotes.length * 20);
    },

    shouldDisplayLegend(activeCard, index) {
      return !(activeCard?.components?.includes('toggles')) && activeCard?.content?.[index]?.config?.legend?.length > 0;
    }
  },
};
</script>
<style lang="scss">
.card-content-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  flex-direction: column;

  &__legends {
    height: 100%;
  }

  > * {
    flex: 1 1 auto;

    &:first-child{
      flex: 1 1 100%;
    }
  }
}
.card-content-container {
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  align-items: stretch;
  flex-wrap: wrap;
}
.small-card {
  &--expand {
    position: fixed !important;
    width: 95vw !important;
    min-height: 95vh !important;
    max-height: 95vh !important;
    top: 2.5vh;
    bottom: 0;
    left: 2.5vw;
    right: 0;
    margin: 0;
    box-shadow: 0 0 118px -12px rgba(0, 0, 0, 0.75);
    z-index: 99 !important;
    background-color: white;
    border-radius: rem(7);
    padding: rem(16) rem(20) rem(20);

    // .small-card__title {
    //   font-size: rem(20) !important;
    // }

    .small-card__controls {
      right: rem(20);
      top: rem(20);

      svg {
        width: rem(22);
      }
    }
  }

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;

    &:first-child {
      margin-left: 0;
      margin-top: 0;
    }
  }

  &__title {
    font-weight: normal;
    font-size: rem(20);
    margin: 0 34px 0 0;
    flex: 0 0 auto;
    padding: 5px 5px 5px 0;
    color: #727272;

    @include iPadProStyles {
      font-size: rem(26);
    }

    &__tile {
      margin-left: 10px;
    }
  }

  &__controls {
    position: absolute;
    right: rem(10);
    top: rem(5);
    z-index: 9;

    .small-card__small-svg {
      width: rem(16);
      cursor: pointer;

      &:not(:last-child) {
        margin-right: rem(10);
      }
    }

    .small-card__expanded-svg {
      width: rem(20);
      cursor: pointer;

      &:not(:last-child) {
        margin-right: rem(10);
      }
    }
  }

  &__content-1 {
    width: 100%;
    height: 100%;
    border: none;
    margin: 0;
  }

  &__content-2 {
    width: calc(50% - 5px);
    height: 100%;
    overflow: hidden;

    // only apply smaller legend styles if there are no tabs in use
    &:not(.small-card__content-tabs) {
      // hacks for the tile view
      // .small-card__title {
      //   font-size: rem(12);
      // }

      .legend-item {
        margin-right: 6px !important;
      }

      .legend-circle {
        width: 5px !important;
        height: 5px !important;
        min-width: 5px !important;
      }

      .legend-text {
        font-size: rem(7) !important;
      }
    }

    // hacks for the modal view to reverse hacks to the tile view
    &.small-card--expand {
      .legend-item {
        margin-right: 10px;
      }
      .legend-circle {
        width: 10px !important;
        height: 10px !important;
        min-width: 10px !important;
      }
      .legend-text {
        font-size: rem(12) !important;
      }
    }
  }

  &__content-4 {
    width: calc(50% - 5px);
    height: calc(50% - 5px);

    // hacks to the tile view
    // .small-card__title {
    //   font-size: rem(14);
    // }

    .legend-text {
      font-size: rem(8) !important;
    }

    // hacks for the modal view to reverse hacks to the tile view
    &.small-card--expand {
      margin: 0;

      // .small-card__title {
      //   font-size: rem(20) !important;
      // }

      .legend-text {
        font-size: rem(12) !important;
      }
    }

    &:not(.small-card--expand) {
      &:nth-child(1) {
        margin: 0 5px 5px 0;
      };

      &:nth-child(2) {
        margin: 0 0 5px 5px;
      };

      &:nth-child(3) {
        margin: 5px 5px 0 0;
      };

      &:nth-child(4) {
        margin: 5px 0 0 5px;
      };
    }
  }

  &__content-6 {
    width: calc(33% - 10px);
    height: calc(50% - 10px);
  }

  &__content-tabs {
    width: 100% !important;
    height: 100% !important;
    border: none;
  }
}

.log {
  @include flexbox(flex-start, space-between);
  pre {
    width: 100%;
  }
}

.outer-chart-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
}

.chart-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
}

.chart {
  flex: 1 1 100%;
}

.legend, .footer {
  flex: 0 1 auto;
}

.tab-hidden {
  display: none;
}
</style>
